import './index.scss';
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import {Box, Card, CircularProgress, MenuItem, Skeleton, SvgIcon, Typography} from "@mui/material";
import IconButton from "../../../../../../../components/base/button/icon_button";
import classnames from "classnames";
import {ReactComponent as ToggleIcon} from "../../../../../../../../assets/images/warranty-statuses-reasons/toggle.svg";
import {Collapse} from "reactstrap";
import Grid from "@mui/material/Unstable_Grid2";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from '../../../../../../../components/form';
import {DeleteOutlined, EditOutlined} from "@mui/icons-material";
import SaveOrRollback from "../../../../../../../components/app-specific/save-or-rollback";
import SearchInput from "../../../../../../../components/base/input/search_input";

const TaxManagementRegionTaxesView = () => {
    const [countries, setCountries] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(true);
    const [loadingTaxes, setLoadingTaxes] = useState(true);
    const [provincesByCountry, setProvincesByCountry] = useState({});
    const [loadingProvincesByCountry, setLoadingProvincesByCountry] = useState({});
    const [openCountries, setOpenCountries] = useState({});
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingCountries || loadingTaxes, [loadingCountries, loadingTaxes]);

    /**
     * Fetches all countries from the API.
     * @type {(function(): Promise<void>)}
     */
    const getCountries = useCallback(async () => {
        setLoadingCountries(true);
        const response = await Api.getAllCountries();
        if (!isMounted())
            return;
        setLoadingCountries(false);
        if (response?.resultFlag) {
            setCountries(response.data ?? []);
        }
    }, [isMounted])

    /**
     * Fetches all the taxes of the system
     * @type {(function(): Promise<void>)}
     */
    const getAllTaxes = useCallback(async () => {
        const paginationInfo = {pageSize: 100, currentPage: 1, length: 0,};
        setLoadingTaxes(true);
        const entries = [];
        do {
            const forApi = {
                pagination: paginationInfo,
            }
            const response = await Api.getSalesTaxes(forApi);
            if (!response?.resultFlag) {
                continue;
            }
            if (!response?.data?.items?.length) {
                break;
            }
            paginationInfo.length = response.data.totalCount;
            entries.push(...(response?.data?.items ?? []));
            if (!isMounted())
                return;
            paginationInfo.currentPage++;
        } while (paginationInfo.length > entries.length);
        setLoadingTaxes(false);
        setTaxes(entries);
    }, [isMounted])

    /**
     * As soon as the component mounts:-
     * - fetches all countries from the API.
     */
    useEffect(() => {
        void getCountries();
        void getAllTaxes();
    }, [getCountries, getAllTaxes]);

    /**
     * Fetches the provinces of a country along with their applied taxes by country Id.
     * @type {(function(*): Promise<void>)|*}
     */
    const getProvincesByCountry = useCallback(async (countryId) => {
        if (provincesByCountry[countryId] || loadingProvincesByCountry[countryId])
            return;
        setLoadingProvincesByCountry(prevState => ({
            ...prevState,
            [countryId]: true,
        }));
        const response = await Api.getProvinceTaxList(countryId);
        if (!isMounted())
            return;
        setLoadingProvincesByCountry(prevState => ({
            ...prevState,
            [countryId]: false,
        }));
        if (response?.resultFlag) {
            setProvincesByCountry(prevState => ({
                ...prevState,
                [countryId]: response.data.map(province => {
                    const appliedTaxesOfThisProvince = province.taxes
                        .map(e => ({
                            id: e.id,
                            tax: taxes.find(tax => tax.code === e.taxCode),
                        }))
                        .filter(e => e.tax);
                    return {
                        id: province.id,
                        name: province.title,
                        appliedTaxes: appliedTaxesOfThisProvince,
                    }
                }),
            }))
        }
    }, [isMounted, loadingProvincesByCountry, provincesByCountry, taxes])

    /**
     * Toggles the open state of a country
     * @type {(function(*): void)}
     */
    const toggleCountryOpenState = useCallback((countryId) => {
        setOpenCountries(prevState => {
            const open = !(prevState[countryId] ?? false);
            if (open) {
                void getProvincesByCountry(countryId);
            }
            return ({
                ...prevState,
                [countryId]: open,
            })
        })
    }, [getProvincesByCountry])


    /**
     * Creates a provincial tax
     * @type {(function(*, *, *): Promise<void>)}
     */
    const createProvincialTax = useCallback(async (countryId, provinceId, tax) => {
        const forApi = {
            provinceId: provinceId,
            taxName: tax.name,
            taxPercentage: tax.rate,
            taxCode: tax.code,
        }
        const response = await Api.addSalesTaxToProvince(forApi);
        if (!isMounted())
            return false
        if (response?.resultFlag) {
            setProvincesByCountry(prevState => ({
                ...prevState,
                [countryId]: prevState[countryId]?.map(province => province.id === provinceId
                    ? {...province, appliedTaxes: [...province.appliedTaxes, {id: response.data.id, tax: tax}]}
                    : province
                ) ?? []
            }));
        }
        return response?.resultFlag;
    }, [isMounted])

    /**
     * Updates a provincial tax
     * @type {(function(*, *, *, *): Promise<void>)}
     */
    const updateProvincialTax = useCallback(async (countryId, provinceId, appliedTaxId, tax) => {
        const forApi = {
            id: appliedTaxId,
            taxName: tax.name,
            taxPercentage: tax.percentage,
            taxCode: tax.code,
        }
        const response = await Api.updateSalesTaxProvince(forApi)
        if (!isMounted())
            return false
        if (response?.resultFlag) {
            setProvincesByCountry(prevState => ({
                ...prevState,
                [countryId]: prevState[countryId]?.map(province => province.id === provinceId
                    ? {
                        ...province,
                        appliedTaxes: province.appliedTaxes.map(appliedTax => appliedTax.id === appliedTaxId
                            ? {...appliedTax, tax: tax,}
                            : appliedTax
                        ),
                    }
                    : province
                ) ?? [],
            }));
        }
        return response?.resultFlag;
    }, [isMounted])

    /**
     * Removes a provincial tax
     * @type {(function(*, *, *): Promise<void>)}
     */
    const removeProvincialTax = useCallback(async (countryId, provinceId, appliedTaxId) => {
        const response = await Api.removeSalesTaxFromProvince(appliedTaxId);
        if (!isMounted())
            return false
        if (response?.resultFlag) {
            setProvincesByCountry(prevState => ({
                ...prevState,
                [countryId]: prevState[countryId]?.map(province => province.id === provinceId
                    ? {...province, appliedTaxes: province.appliedTaxes.filter(appliedTax => appliedTax.id !== appliedTaxId)}
                    : province
                ) ?? [],
            }))
        }
        return response?.resultFlag;
    }, [isMounted])

    return (
        <>
            <div className={'d-flex flex-column w-100 mt-3'} style={{gap: 24}}>
                {
                    loading &&
                    Array.from({length: 5}, (_, i) => (
                        <div key={`loading-${i}`} className={'loading large'}>
                            <div/>
                        </div>
                    ))
                }
                {
                    !loading &&
                    countries.map(country => (
                        <TaxManagementRegionTaxesViewCountryCard
                            key={country.id}
                            country={country}
                            taxes={taxes}
                            provinces={provincesByCountry[country.id]}
                            loading={loadingProvincesByCountry[country.id]}
                            open={openCountries[country.id]}
                            toggleCountryOpenState={toggleCountryOpenState}
                            createProvincialTax={createProvincialTax}
                            updateProvincialTax={updateProvincialTax}
                            removeProvincialTax={removeProvincialTax}
                        />
                    ))
                }
            </div>
        </>
    );
}

const TaxManagementRegionTaxesViewCountryCard = ({
                                                     country,
                                                     provinces,
                                                     taxes,
                                                     loading,
                                                     open,
                                                     toggleCountryOpenState,
                                                     updateProvincialTax,
                                                     removeProvincialTax,
                                                     createProvincialTax
                                                 }) => {
    const [keywords, setKeywords] = useState('');

    const filteredProvinces = useMemo(() => {
        return provinces?.filter(province =>
            province.name.toLowerCase().includes(keywords.toLowerCase()) ||
            keywords === ''
        );
    }, [keywords, provinces])

    return (
        <>
            <Card variant={'elevation'} sx={{boxShadow: 'none', padding: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBlockEnd: open ? 3 : 0}}>
                    <Typography variant={'subtitle1'} fontWeight={600} color={'tertiary.main'}>
                        {country.name ?? ""}
                    </Typography>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                        <IconButton
                            className={'toggle-button'}
                            onPressed={() => toggleCountryOpenState(country.id)}
                        >
                            <SvgIcon
                                component={ToggleIcon}
                                className={classnames('icon normal toggle-icon', {'open': open})}
                            />
                        </IconButton>
                    </Box>
                </Box>
                <Collapse isOpen={open}>
                    <Grid container spacing={2}>
                        <Grid xs={12}>
                            <SearchInput
                                paperProps={{
                                    sx: {padding: `0px !important`}
                                }}
                                autoSearch
                                fullWidth
                                loading={loading}
                                search={setKeywords}
                                variant={'outlined'}
                                label={'Search Provinces'}
                                initialValue={keywords}
                            />
                        </Grid>
                        {
                            loading &&
                            Array.from({length: 5}, (_, i) => (
                                <Grid item xs={12} sm={6} lg={4} xl={3} key={`loading-${i}`}>
                                    <Skeleton variant={'rounded'} height={306.5} width={'100%'}/>
                                </Grid>
                            ))
                        }
                        {
                            !loading &&
                            filteredProvinces?.map(province => (
                                <Grid item xs={12} sm={6} lg={4} xl={3} key={province.id}>
                                    <TaxManagementRegionTaxesViewProvinceCard
                                        countryId={country.id}
                                        province={province}
                                        taxes={taxes}
                                        createProvincialTax={createProvincialTax}
                                        updateProvincialTax={updateProvincialTax}
                                        removeProvincialTax={removeProvincialTax}
                                    />
                                </Grid>
                            ))
                        }
                        {
                            !loading &&
                            <>
                                {
                                    !provinces?.length &&
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant={'caption'} color={'text.disabled'} textAlign={'center'} width={'100%'}>
                                                No provinces found for this country.
                                            </Typography>
                                        </Grid>
                                    </>
                                }
                                {
                                    !!provinces?.length && !filteredProvinces?.length &&
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant={'caption'} color={'text.disabled'} textAlign={'center'} width={'100%'}>
                                                No provinces found for your searched keyword.
                                            </Typography>
                                        </Grid>
                                    </>
                                }
                            </>
                        }
                    </Grid>
                </Collapse>
            </Card>
        </>
    );
}

const TaxManagementRegionTaxesViewProvinceCard = ({
                                                      countryId,
                                                      province,
                                                      taxes,
                                                      createProvincialTax,
                                                      updateProvincialTax,
                                                      removeProvincialTax
                                                  }) => {
    const availableTaxes = useMemo(() => taxes.filter(tax => !province.appliedTaxes.find(appliedTax => appliedTax.tax.id === tax.id)), [province, taxes]);
    const canAdd = useMemo(() => availableTaxes.length > 0 && province.appliedTaxes.length < 4, [availableTaxes, province]);

    return (
        <>
            <Card
                variant={'outlined'}
                sx={{padding: 1, display: 'flex', flexDirection: 'column', borderColor: `divider`, height: '100%'}}
            >
                <Typography variant={'body2'} fontWeight={600}>
                    {province.name ?? ""}
                </Typography>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, marginBlockStart: 2, flexGrow: 1}}>
                    {
                        province.appliedTaxes.map(appliedTax => (
                            <TaxManagementRegionTaxesViewTaxCard
                                key={appliedTax.id}
                                countryId={countryId}
                                provinceId={province.id}
                                tax={appliedTax.tax}
                                id={appliedTax.id}
                                taxOptions={availableTaxes}
                                taxes={taxes}
                                createProvincialTax={createProvincialTax}
                                updateProvincialTax={updateProvincialTax}
                                removeProvincialTax={removeProvincialTax}
                            />
                        ))
                    }
                    <Box sx={{flexGrow: 1}}/>
                    {
                        canAdd &&
                        <TaxManagementRegionTaxesViewTaxCard
                            countryId={countryId}
                            provinceId={province.id}
                            taxOptions={availableTaxes}
                            taxes={taxes}
                            createProvincialTax={createProvincialTax}
                            updateProvincialTax={updateProvincialTax}
                            removeProvincialTax={removeProvincialTax}
                        />
                    }
                </Box>
            </Card>
        </>
    );
}

const TaxManagementRegionTaxesViewTaxCard = ({
                                                 tax,
                                                 id,
                                                 provinceId,
                                                 countryId,
                                                 taxOptions,
                                                 taxes,
                                                 createProvincialTax,
                                                 updateProvincialTax,
                                                 removeProvincialTax,
                                             }) => {
    const [submitting, setSubmitting] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [inEditMode, setInEditMode] = useState(!id);
    const isMounted = useIsMounted();
    const formRef = useRef(null);

    const schema = useMemo(() => (
        Yup.object().shape({
            taxId: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('Tax')),
        })
    ), []);
    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        taxId: tax?.id ?? '',
    }), [tax]);

    /**
     * Submits the changes to the API
     * @type {(function(*, *): Promise<void>)}
     */
    const submitChanges = useCallback(async (values, form) => {
        setSubmitting(true);
        if (id) {
            const resultFlag = await updateProvincialTax(countryId, provinceId, id, taxOptions.find(tax => tax.id === values.taxId));
            if (!isMounted())
                return
            setSubmitting(false);
            if (resultFlag) {
                setInEditMode(false);
            }
        } else {
            const resultFlag = await createProvincialTax(countryId, provinceId, taxOptions.find(tax => tax.id === values.taxId));
            if (!isMounted())
                return
            setSubmitting(false);
            if (resultFlag) {
                form.reset(initialValues);
                form.resetFieldState('taxId');
            }
        }
    }, [countryId, createProvincialTax, id, initialValues, isMounted, provinceId, taxOptions, updateProvincialTax]);

    /**
     * Rolls back the values of the form to its initial state.
     */
    const rollbackValues = useCallback((form) => {
        if (id) {
            setInEditMode(false);
        }
        form.reset(initialValues);
        form.resetFieldState('taxId');
    }, [initialValues, id]);

    /**
     * Removes the entry from the API
     * @type {(function(): Promise<void>)}
     */
    const removeEntry = useCallback(async () => {
        setRemoving(true);
        await removeProvincialTax(countryId, provinceId, id);
        if (!isMounted())
            return
        setRemoving(false);
    }, [isMounted, id, countryId, provinceId, removeProvincialTax])

    /**
     * Resets the form if the selected tax is not found in the tax options anymore.
     */
    useEffect(() => {
        const found = taxOptions.find(tax => tax.id === formRef.current?.values?.taxId);
        if (!found) {
            formRef.current?.form?.reset();
            formRef.current?.form?.resetFieldState('taxId');
        }
    }, [taxOptions]);

    return (
        <>
            <Card
                variant={'outlined'}
                sx={{display: 'flex', justifyContent: 'space-between', padding: 0, width: '100%'}}
                className={'tax-management-region-taxes-tax-card'}
            >
                <Form
                    onSubmit={submitChanges}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{
                        className: 'w-100',
                    }}
                >
                    {({form, values}) => {
                        formRef.current = {form, values};
                        return (
                            <Grid container spacing={2} marginInline={0} marginBlock={0}>
                                <Grid xs>
                                    <MuiRFF.TextField
                                        fullWidth
                                        select
                                        size={'small'}
                                        name={"taxId"}
                                        label={id ? 'Tax' : "New Tax"}
                                        required={required.content}
                                        SelectProps={{
                                            renderValue: (value) => {
                                                const tax = taxes.find(tax => tax.id === value);
                                                if (!tax)
                                                    return '';
                                                return `${tax.name} (${tax.rate}%)`;
                                            },
                                        }}
                                        className={classnames({'show-as-text': !inEditMode})}
                                    >
                                        {
                                            taxOptions.map(tax => (
                                                <MenuItem key={tax.id} value={tax.id}>
                                                    {tax.name} ({tax.rate}%)
                                                </MenuItem>
                                            ))
                                        }
                                    </MuiRFF.TextField>
                                </Grid>
                                <Grid xs={'auto'}>
                                    {
                                        !inEditMode &&
                                        <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5}}>
                                            <IconButton
                                                disabled={removing}
                                                size={'small'}
                                                onClick={() => setInEditMode(true)}
                                                color={'primary'}
                                                sx={{backgroundColor: 'background.paper'}}
                                            >
                                                <EditOutlined/>
                                            </IconButton>
                                            <IconButton
                                                onClick={removeEntry}
                                                disabled={removing}
                                                color={'error'}
                                                size={'small'}
                                                sx={{backgroundColor: 'background.paper'}}
                                            >
                                                {
                                                    removing
                                                        ? <CircularProgress size={15} color={'primary'}/>
                                                        : <DeleteOutlined/>
                                                }
                                            </IconButton>
                                        </Box>
                                    }
                                    {
                                        inEditMode &&
                                        <SaveOrRollback
                                            show
                                            column={false}
                                            showText={false}
                                            onSave={() => form.submit()}
                                            onRollback={() => rollbackValues(form)}
                                            loading={submitting}
                                        />
                                    }
                                </Grid>
                            </Grid>
                        )
                    }}
                </Form>
            </Card>
        </>
    );
}

export default TaxManagementRegionTaxesView;
